import React, { useState, useEffect, useContext } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  CreditCard, 
  MessageCircle, 
  Bitcoin,
  DollarSign,
  Edit3,
  Save,
  X,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Send,
  Headphones,
  FileText,
  Upload,
  Download,
  File,
  Sparkles,
  Key,
  Shield
} from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TradingManagementPage = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('deposit');
  const { tt } = useLanguage();

  // Проверка прав доступа
  const canEditPaymentDetails = () => {
    return user?.role === 'superadmin';
  };

  const canEditPlatformSettings = () => {
    return user?.role === 'superadmin';
  };

  const canSendMessages = () => {
    return ['superadmin', 'admin', 'team_lead_retention'].includes(user?.role);
  };
  const [activeDepositTab, setActiveDepositTab] = useState('visa_mastercard');
  const [loading, setLoading] = useState(false);
  
  // Deposit methods state
  const [depositMethods, setDepositMethods] = useState([]);
  const [editingMethod, setEditingMethod] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    details: '',
    redirect_url: '',
    enabled: true
  });

  // Messages state
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [clientMessages, setClientMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  // Support contacts state
  const [supportContacts, setSupportContacts] = useState({
    telegram: '@InvestedsCripto_bot',
    whatsapp: '+77754156437',
    telegram_enabled: true,
    whatsapp_enabled: true
  });
  const [editingSupportContacts, setEditingSupportContacts] = useState(false);
  const [supportForm, setSupportForm] = useState({
    telegram: '',
    whatsapp: '',
    telegram_enabled: true,
    whatsapp_enabled: true
  });

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    description: '',
    file: null,
    enabled: true
  });

  // Brand settings state
  const [brandSettings, setBrandSettings] = useState({
    brand_name: localStorage.getItem('brandName') || ''
  });
  const [editingBrand, setEditingBrand] = useState(false);
  const [brandForm, setBrandForm] = useState({
    brand_name: ''
  });

  // Master password state (только для суперадмина)
  const [hasMasterPassword, setHasMasterPassword] = useState(false);
  const [editingMasterPassword, setEditingMasterPassword] = useState(false);
  const [masterPasswordForm, setMasterPasswordForm] = useState({
    current_password: '',
    master_password: ''
  });
  const [showMasterPassword, setShowMasterPassword] = useState(false);

  useEffect(() => {
    fetchDepositMethods();
    fetchClients();
    fetchSupportContacts();
    fetchDocuments();
    fetchBrandSettings();
    if (user?.role === 'superadmin') {
      fetchMasterPasswordStatus();
    }
  }, [user]);

  const fetchDepositMethods = async () => {
    try {
      const response = await axios.get(`${API}/deposit-methods`);
      setDepositMethods(response.data);
    } catch (error) {
      console.error('Error fetching deposit methods:', error);
    }
  };

  const fetchMasterPasswordStatus = async () => {
    try {
      const response = await axios.get(`${API}/brand/master-password-status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setHasMasterPassword(response.data.has_master_password);
    } catch (error) {
      console.error('Error fetching master password status:', error);
    }
  };

  const handleSaveMasterPassword = async () => {
    if (!masterPasswordForm.current_password) {
      alert(tt('Введите ваш текущий пароль для подтверждения'));
      return;
    }

    try {
      setLoading(true);
      await axios.put(`${API}/brand/master-password`, masterPasswordForm, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      await fetchMasterPasswordStatus();
      setEditingMasterPassword(false);
      setMasterPasswordForm({ current_password: '', master_password: '' });
      alert(masterPasswordForm.master_password ? tt('Мастер-пароль обновлён!') : tt('Мастер-пароль удалён!'));
    } catch (error) {
      console.error('Error updating master password:', error);
      alert(error.response?.data?.detail || tt('Ошибка при обновлении мастер-пароля'));
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      // OPTIMIZED: Load 1000 instead of 10000 for faster performance
      const response = await axios.get(`${API}/clients?skip=0&limit=1000`);
      const clientsData = response.data.items || response.data;
      setClients(clientsData);
      setFilteredClients(clientsData);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };


  const fetchSupportContacts = async () => {
    try {
      const response = await axios.get(`${API}/support/contacts/admin`);
      setSupportContacts(response.data);
      setSupportForm(response.data);
    } catch (error) {
      console.error('Error fetching support contacts:', error);
    }
  };

  const handleSaveSupportContacts = async () => {
    try {
      setLoading(true);
      await axios.put(`${API}/support/contacts`, supportForm);
      await fetchSupportContacts();
      setEditingSupportContacts(false);
      alert(tt('Контакты поддержки обновлены'));
    } catch (error) {
      console.error('Error updating support contacts:', error);
      alert(tt('Ошибка при обновлении контактов поддержки'));
    } finally {
      setLoading(false);
    }
  };

  // Documents functions
  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/platform-documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleUploadDocument = async () => {
    if (!documentForm.title || !documentForm.file) {
      alert(tt('Заполните название и выберите файл'));
      return;
    }

    try {
      setUploadingDocument(true);
      const formData = new FormData();
      formData.append('title', documentForm.title);
      formData.append('description', documentForm.description || '');
      formData.append('enabled', documentForm.enabled);
      formData.append('file', documentForm.file);

      await axios.post(`${API}/platform-documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      alert(tt('Документ загружен успешно'));
      setDocumentForm({ title: '', description: '', file: null, enabled: true });
      await fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(tt('Ошибка загрузки: ') + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm(tt('Удалить этот документ?'))) return;

    try {
      await axios.delete(`${API}/platform-documents/${docId}`);
      alert(tt('Документ удален'));
      await fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(tt('Ошибка удаления документа'));
    }
  };

  const handleToggleDocumentStatus = async (docId, currentStatus) => {
    try {
      await axios.patch(`${API}/platform-documents/${docId}`, null, {
        params: { enabled: !currentStatus }
      });
      await fetchDocuments();
    } catch (error) {
      console.error('Error toggling document status:', error);
      alert(tt('Ошибка изменения статуса'));
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return <File className="w-6 h-6 text-red-600" />;
      case 'image':
        return <File className="w-6 h-6 text-blue-600" />;
      case 'word':
        return <FileText className="w-6 h-6 text-blue-800" />;
      case 'excel':
        return <FileText className="w-6 h-6 text-green-600" />;
      case 'text':
        return <FileText className="w-6 h-6 text-gray-600" />;
      default:
        return <File className="w-6 h-6 text-gray-400" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Brand settings functions
  const fetchBrandSettings = async () => {
    try {
      const response = await axios.get(`${API}/brand/settings`);
      setBrandSettings(response.data);
      setBrandForm({ brand_name: response.data.brand_name });
    } catch (error) {
      console.error('Error fetching brand settings:', error);
    }
  };

  const handleSaveBrandSettings = async () => {
    if (!brandForm.brand_name.trim()) {
      alert(tt('Введите название бренда'));
      return;
    }

    try {
      setLoading(true);
      await axios.put(`${API}/brand/settings`, brandForm);
      await fetchBrandSettings();
      setEditingBrand(false);
      alert(tt('Настройки бренда обновлены! Обновите страницы сайта чтобы увидеть изменения.'));
    } catch (error) {
      console.error('Error updating brand settings:', error);
      alert(tt('Ошибка при обновлении настроек бренда'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchClients = () => {
    if (!clientSearch.trim()) {
      setFilteredClients(clients);
      return;
    }
    
    const searchTerm = clientSearch.toLowerCase();
    const filtered = clients.filter(client => 
      client.id.toString().includes(searchTerm) ||
      (client.name && client.name.toLowerCase().includes(searchTerm)) ||
      (client.email && client.email.toLowerCase().includes(searchTerm)) ||
      (client.phone && client.phone.toLowerCase().includes(searchTerm))
    );
    setFilteredClients(filtered);
  };

  const fetchClientMessages = async (clientId) => {
    try {
      const response = await axios.get(`${API}/client-messages/${clientId}`);
      setClientMessages(response.data);
    } catch (error) {
      console.error('Error fetching client messages:', error);
      setClientMessages([]);
    }
  };

  const handleEditMethod = (method) => {
    setEditingMethod(method.id);
    setEditForm({
      title: method.title,
      description: method.description,
      details: method.details,
      redirect_url: method.redirect_url || '',
      enabled: method.enabled
    });
  };

  const handleSaveMethod = async (methodId) => {
    try {
      setLoading(true);
      await axios.put(`${API}/deposit-methods/${methodId}`, editForm);
      await fetchDepositMethods();
      setEditingMethod(null);
    } catch (error) {
      console.error('Error updating deposit method:', error);
      alert(tt('Ошибка при обновлении способа пополнения'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMethod = async (methodId, enabled) => {
    try {
      await axios.put(`${API}/deposit-methods/${methodId}`, { enabled });
      await fetchDepositMethods();
    } catch (error) {
      console.error('Error toggling deposit method:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedClientId || !newMessage.trim()) {
      alert(tt('Выберите клиента и введите сообщение'));
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/client-messages`, {
        client_id: selectedClientId,
        content: newMessage.trim()
      });
      setNewMessage('');
      await fetchClientMessages(selectedClientId);
      alert(tt('Сообщение отправлено'));
    } catch (error) {
      console.error('Error sending message:', error);
      alert(tt('Ошибка при отправке сообщения'));
    } finally {
      setLoading(false);
    }
  };

  const getMethodsByType = (type) => {
    return depositMethods.filter(method => method.type === type);
  };

  const getDepositIcon = (type) => {
    switch (type) {
      case 'exchange':
        return <DollarSign className="w-6 h-6" />;
      case 'visa_mastercard':
      case 'bank_card':
        return <CreditCard className="w-6 h-6" />;
      case 'crypto':
        return <Bitcoin className="w-6 h-6" />;
      default:
        return <DollarSign className="w-6 h-6" />;
    }
  };

  const depositTabs = [
    {
      key: 'exchange',
      label: tt('Обмен'),
      description: tt('Пополнение через обменник')
    },
    {
      key: 'visa_mastercard',
      label: 'VISA/MasterCard',
      description: tt('Банковские карты Visa, MasterCard')
    },
    {
      key: 'bank_card',
      label: tt('Карта'),
      description: tt('Дебетовые и кредитные карты')
    },
    {
      key: 'crypto',
      label: tt('Криптовалюта'),
      description: 'Bitcoin, Ethereum, USDT'
    }
  ];

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{tt('Торговая платформа')}</h1>
          <p className="page-subtitle">{tt('Управление способами пополнения и сообщениями клиентам')}</p>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" role="tablist">
            <button
              onClick={() => setActiveTab('deposit')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'deposit'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <CreditCard className="w-4 h-4" />
                <span>{tt('Пополнение')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'messages'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <MessageCircle className="w-4 h-4" />
                <span>{tt('Сообщения клиенту')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('support')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'support'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Headphones className="w-4 h-4" />
                <span>{tt('Поддержка')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>{tt('Документы')}</span>
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'deposit' && (
            <div>
              {/* Deposit Method Sub-tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-6">
                  {depositTabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveDepositTab(tab.key)}
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeDepositTab === tab.key
                          ? 'border-emerald-500 text-emerald-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {getDepositIcon(tab.key)}
                        <div className="text-left">
                          <div>{tab.label}</div>
                          <div className="text-xs text-gray-400">{tab.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Deposit Method Content */}
              <div>
                {getMethodsByType(activeDepositTab).length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>{tt('Способы пополнения типа')} "{depositTabs.find(t => t.key === activeDepositTab)?.label}" {tt('не настроены')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getMethodsByType(activeDepositTab).map((method) => (
                      <div key={method.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            {getDepositIcon(method.type)}
                            <div>
                              <h3 className="font-medium text-gray-900">{method.title}</h3>
                              <p className="text-sm text-gray-500">{method.description}</p>
                            </div>
                          </div>
                          {canEditPaymentDetails() && (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleToggleMethod(method.id, !method.enabled)}
                                className={`p-2 rounded-lg transition-colors ${
                                  method.enabled 
                                    ? 'text-green-600 hover:bg-green-100' 
                                    : 'text-gray-400 hover:bg-gray-200'
                                }`}
                                title={method.enabled ? tt('Отключить') : tt('Включить')}
                              >
                                {method.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => handleEditMethod(method)}
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                title={tt('Редактировать')}
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>

                        {editingMethod === method.id ? (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tt('Название')}
                              </label>
                              <input
                                type="text"
                                value={editForm.title}
                                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tt('Описание')}
                              </label>
                              <input
                                type="text"
                                value={editForm.description}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            
                            {method.type === 'exchange' ? (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {tt('URL для редиректа')}
                                </label>
                                <input
                                  type="url"
                                  value={editForm.redirect_url}
                                  onChange={(e) => setEditForm({ ...editForm, redirect_url: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="https://example.com/exchange?amount={amount}"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  {tt('Используйте')} {'{amount}'} {tt('для передачи суммы пополнения')}
                                </p>
                              </div>
                            ) : (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {tt('Реквизиты')}
                                </label>
                                <textarea
                                  value={editForm.details}
                                  onChange={(e) => setEditForm({ ...editForm, details: e.target.value })}
                                  rows={4}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder={tt('Введите реквизиты для пополнения (номера карт, кошельки, инструкции)')}
                                />
                              </div>
                            )}
                            
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => setEditingMethod(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                disabled={loading}
                              >
                                <X className="w-4 h-4 mr-1 inline" />
                                {tt('Отмена')}
                              </button>
                              <button
                                onClick={() => handleSaveMethod(method.id)}
                                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:bg-gray-400"
                                disabled={loading}
                              >
                                <Save className="w-4 h-4 mr-1 inline" />
                                {tt('Сохранить')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white rounded-lg p-4 border">
                            {method.type === 'exchange' ? (
                              <>
                                <h4 className="font-medium text-gray-900 mb-2">{tt('URL для редиректа:')}</h4>
                                <p className="text-sm text-gray-600 break-all">
                                  {method.redirect_url || tt('URL не указан')}
                                </p>
                              </>
                            ) : (
                              <>
                                <h4 className="font-medium text-gray-900 mb-2">{tt('Реквизиты:')}</h4>
                                <pre className="text-sm text-gray-600 whitespace-pre-wrap font-mono">
                                  {method.details || tt('Реквизиты не указаны')}
                                </pre>
                              </>
                            )}
                            <div className="mt-3 flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                method.enabled 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {method.enabled ? (
                                  <>
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    {tt('Включен')}
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-3 h-3 mr-1" />
                                    {tt('Отключен')}
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Информация о правах доступа */}
                {!canEditPaymentDetails() && (
                  <div className="mt-6 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    <p className="text-sm">
                      <strong>{tt('Информация:')}</strong> {tt('Редактирование реквизитов доступно только супер-админам.')}
                      {tt('Ваша роль:')} <span className="font-semibold">{user?.role}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'messages' && (
            <div>
              {canSendMessages() ? (
                <div className="mb-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">{tt('Отправить сообщение клиенту')}</h2>
                
                {/* Client Search */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tt('Поиск клиента')}
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      placeholder={tt('ID, ФИО, телефон, email')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchClients()}
                    />
                    <button
                      onClick={handleSearchClients}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      {tt('Искать')}
                    </button>
                  </div>
                </div>

                {/* Client Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tt('Выберите клиента')}
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => {
                      setSelectedClientId(e.target.value);
                      if (e.target.value) {
                        fetchClientMessages(e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">{tt('Выберите клиента')}</option>
                    {filteredClients.map((client) => (
                      <option key={client.id} value={client.id}>
                        ID: {client.id} - {client.name} ({client.email}) - {client.phone}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tt('Сообщение')}
                  </label>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder={tt('Введите сообщение для клиента...')}
                  />
                </div>

                <button
                  onClick={handleSendMessage}
                  disabled={loading || !selectedClientId || !newMessage.trim()}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4 mr-2 inline" />
                  {tt('Отправить сообщение')}
                </button>

                {/* Message History */}
                {selectedClientId && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{tt('История сообщений')}</h3>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                    {clientMessages.length === 0 ? (
                      <p className="text-gray-500 text-center">{tt('Нет сообщений для данного клиента')}</p>
                    ) : (
                      <div className="space-y-3">
                        {clientMessages.map((message) => (
                          <div key={message.id} className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-sm font-medium text-blue-600">
                                {message.created_by_name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(message.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-gray-700">{message.message}</p>
                            <div className="mt-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                message.read 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {message.read ? tt('Прочитано') : tt('Не прочитано')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
              ) : (
                <div className="text-center py-8">
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <strong className="font-bold">{tt('Доступ запрещен!')}</strong>
                    <span className="block sm:inline"> {tt('Отправка сообщений клиентам доступна только для супер-админов, админов и тим-лидеров retention.')}</span>
                  </div>
                  <p className="text-gray-500">{tt('Ваша роль:')} <span className="font-semibold">{user?.role}</span></p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'support' && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">{tt('Управление контактами поддержки')}</h2>
              <p className="text-gray-600 mb-6">
                {tt('Настройте контакты поддержки, которые будут отображаться на сайте и в торговой платформе')}
              </p>

              {/* Current Contacts Display */}
              {!editingSupportContacts && (
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4">{tt('Текущие контакты')}</h3>
                  
                  <div className="space-y-4">
                    {/* Telegram */}
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Telegram</p>
                          <p className="text-sm text-gray-600">{supportContacts.telegram}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {canEditPlatformSettings() && (
                        <button
                          onClick={async () => {
                            const newEnabled = !supportContacts.telegram_enabled;
                            try {
                              setLoading(true);
                              await axios.put(`${API}/support/contacts`, {
                                telegram_enabled: newEnabled
                              });
                              await fetchSupportContacts();
                            } catch (error) {
                              console.error('Error toggling telegram:', error);
                              alert(tt('Ошибка при изменении статуса'));
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className={`p-2 rounded-lg transition-colors ${
                            supportContacts.telegram_enabled
                              ? 'bg-green-100 text-green-600 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                          title={supportContacts.telegram_enabled ? tt('Скрыть') : tt('Показать')}
                        >
                          {supportContacts.telegram_enabled ? (
                            <Eye className="w-5 h-5" />
                          ) : (
                            <EyeOff className="w-5 h-5" />
                          )}
                        </button>
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          supportContacts.telegram_enabled 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {supportContacts.telegram_enabled ? tt('Отображается') : tt('Скрыт')}
                        </span>
                      </div>
                    </div>

                    {/* WhatsApp */}
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">WhatsApp</p>
                          <p className="text-sm text-gray-600">{supportContacts.whatsapp}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {canEditPlatformSettings() && (
                        <button
                          onClick={async () => {
                            const newEnabled = !supportContacts.whatsapp_enabled;
                            try {
                              setLoading(true);
                              await axios.put(`${API}/support/contacts`, {
                                whatsapp_enabled: newEnabled
                              });
                              await fetchSupportContacts();
                            } catch (error) {
                              console.error('Error toggling whatsapp:', error);
                              alert(tt('Ошибка при изменении статуса'));
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className={`p-2 rounded-lg transition-colors ${
                            supportContacts.whatsapp_enabled
                              ? 'bg-green-100 text-green-600 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                          title={supportContacts.whatsapp_enabled ? tt('Скрыть') : tt('Показать')}
                        >
                          {supportContacts.whatsapp_enabled ? (
                            <Eye className="w-5 h-5" />
                          ) : (
                            <EyeOff className="w-5 h-5" />
                          )}
                        </button>
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          supportContacts.whatsapp_enabled 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {supportContacts.whatsapp_enabled ? tt('Отображается') : tt('Скрыт')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {canEditPlatformSettings() && (
                  <button
                    onClick={() => setEditingSupportContacts(true)}
                    className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit3 className="w-4 h-4 inline mr-2" />
                    {tt('Редактировать контакты')}
                  </button>
                  )}
                </div>
              )}

              {/* Edit Form */}
              {editingSupportContacts && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4">{tt('Редактирование контактов')}</h3>
                  
                  <div className="space-y-4">
                    {/* Telegram Settings */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={supportForm.telegram_enabled}
                            onChange={(e) => setSupportForm({ ...supportForm, telegram_enabled: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-900">{tt('Показать Telegram')}</span>
                        </label>
                      </div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {tt('Telegram контакт')}
                      </label>
                      <input
                        type="text"
                        value={supportForm.telegram}
                        onChange={(e) => setSupportForm({ ...supportForm, telegram: e.target.value })}
                        placeholder={tt('@username или t.me/username')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">{tt('Например: @InvestedsCripto_bot')}</p>
                    </div>

                    {/* WhatsApp Settings */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={supportForm.whatsapp_enabled}
                            onChange={(e) => setSupportForm({ ...supportForm, whatsapp_enabled: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-900">{tt('Показать WhatsApp')}</span>
                        </label>
                      </div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {tt('WhatsApp номер')}
                      </label>
                      <input
                        type="text"
                        value={supportForm.whatsapp}
                        onChange={(e) => setSupportForm({ ...supportForm, whatsapp: e.target.value })}
                        placeholder="+77754156437"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">{tt('Например: +77754156437')}</p>
                    </div>
                  </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      onClick={handleSaveSupportContacts}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                    >
                      <Save className="w-4 h-4 inline mr-2" />
                      {tt('Сохранить изменения')}
                    </button>
                    <button
                      onClick={() => {
                        setEditingSupportContacts(false);
                        setSupportForm(supportContacts);
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      <X className="w-4 h-4 inline mr-2" />
                      {tt('Отмена')}
                    </button>
                  </div>
                </div>
              )}

              {/* Info Block */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">ℹ️ {tt('Информация')}</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• {tt('Контакты отображаются в разделе "Поддержка" на сайте и в торговой платформе')}</li>
                  <li>• {tt('Вы можете скрыть любой контакт, сняв галочку "Показать"')}</li>
                  <li>• {tt('При клике на контакт пользователь будет перенаправлен в соответствующее приложение')}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">{tt('Управление документами платформы')}</h2>
              <p className="text-gray-600 mb-6">
                {tt('Загружайте документы, которые будут доступны клиентам на торговой платформе')}
              </p>

              {/* Upload Form */}
              {canEditPlatformSettings() && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-md font-medium text-gray-900 mb-4">{tt('Загрузить новый документ')}</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {tt('Название документа')} *
                    </label>
                    <input
                      type="text"
                      value={documentForm.title}
                      onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                      placeholder={tt('Например: Пользовательское соглашение')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {tt('Описание')} ({tt('опционально')})
                    </label>
                    <textarea
                      value={documentForm.description}
                      onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                      placeholder={tt('Краткое описание документа')}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {tt('Файл')} *
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.txt,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => setDocumentForm({ ...documentForm, file: e.target.files[0] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {tt('Поддерживаются: PDF, JPEG, PNG, TXT, Word, Excel')}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="doc-enabled"
                      checked={documentForm.enabled}
                      onChange={(e) => setDocumentForm({ ...documentForm, enabled: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="doc-enabled" className="text-sm text-gray-700">
                      {tt('Показывать клиентам сразу после загрузки')}
                    </label>
                  </div>

                  <button
                    onClick={handleUploadDocument}
                    disabled={uploadingDocument || !documentForm.title || !documentForm.file}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                  >
                    {uploadingDocument ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {tt('Загрузка...')}
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Upload className="w-4 h-4 mr-2" />
                        {tt('Загрузить документ')}
                      </span>
                    )}
                  </button>
                </div>
              </div>
              )}

              {/* Documents List */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4">{tt('Загруженные документы')} ({documents.length})</h3>
                
                {documents.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">{tt('Нет загруженных документов')}</p>
                    <p className="text-sm text-gray-400 mt-1">{tt('Загрузите первый документ выше')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            {getFileIcon(doc.file_type)}
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{doc.title}</h4>
                              {doc.description && (
                                <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                              )}
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                <span>{doc.filename}</span>
                                <span>•</span>
                                <span>{formatFileSize(doc.file_size)}</span>
                                <span>•</span>
                                <span>{new Date(doc.created_at).toLocaleDateString('ru-RU')}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-4">
                            {canEditPlatformSettings() && (
                            <button
                              onClick={() => handleToggleDocumentStatus(doc.id, doc.enabled)}
                              className={`p-2 rounded-lg transition-colors ${
                                doc.enabled
                                  ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                              title={doc.enabled ? tt('Скрыть от клиентов') : tt('Показать клиентам')}
                            >
                              {doc.enabled ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                            </button>
                            )}
                            <a
                              href={`${API}/platform-documents/${doc.id}/view`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors"
                              title={tt('Просмотреть')}
                            >
                              <Eye className="w-5 h-5" />
                            </a>
                            <a
                              href={`${API}/platform-documents/${doc.id}/download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                              title={tt('Скачать')}
                            >
                              <Download className="w-5 h-5" />
                            </a>
                            {canEditPlatformSettings() && (
                            <button
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                              title={tt('Удалить')}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            doc.enabled 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {doc.enabled ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {tt('Показывается клиентам')}
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3 mr-1" />
                                {tt('Скрыт от клиентов')}
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Block */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">ℹ️ {tt('Информация')}</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• {tt('Документы отображаются в разделе "Документы" торговой платформы')}</li>
                  <li>• {tt('Клиенты могут просматривать и скачивать документы')}</li>
                  <li>• {tt('Вы можете скрыть документ, нажав на иконку глаза')}</li>
                  <li>• {tt('Максимальный размер файла: 10 МБ')}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Brand Settings Tab */}
          {activeTab === 'brand' && (
            <div className="text-center py-8 text-gray-500">
              <p>{tt('Настройки бренда перенесены на Главную страницу CRM')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingManagementPage;